(() => {
  const GLOBAL_KEY = '__pdlProductLocationUnifier';

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const state = window[GLOBAL_KEY] || {
    searchCache: new Map(),
    productCache: new Map(),
    boundBlocks: new WeakSet(),
  };
  window[GLOBAL_KEY] = state;

  const previewThemeId = () => new URL(window.location.href).searchParams.get('preview_theme_id') || '';

  const routeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const currentVariantId = (block, product) => {
    const scope = block.closest('.product-details') || block.closest('.shopify-section') || document;
    const input = scope.querySelector('product-form-component input[name="id"]');
    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    const requested = String(fromUrl || input?.value || '');
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const selected = variants.find((variant) => String(variant.id) === requested)
      || variants.find((variant) => variant.available)
      || variants[0]
      || null;
    return selected ? String(selected.id) : '';
  };

  const parseBlockProduct = (block) => {
    const script = block.querySelector('script[data-pdl-current-product]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[Pedalokos] Dados do produto inválidos para unificação de localização.', error);
      return null;
    }
  };

  const searchProducts = async (query, fields) => {
    const cleanQuery = String(query || '').trim();
    if (!cleanQuery) return [];

    const cacheKey = `${fields}|${cleanQuery}`;
    if (state.searchCache.has(cacheKey)) return state.searchCache.get(cacheKey);

    const request = (async () => {
      const params = new URLSearchParams({ q: cleanQuery });
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'show');
      params.set('resources[options][fields]', fields);

      const response = await fetch(`${routeRoot()}search/suggest.json?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Busca HTTP ${response.status}`);
      const payload = await response.json();
      return payload?.resources?.results?.products || [];
    })().catch((error) => {
      console.warn('[Pedalokos] Falha ao localizar produtos relacionados.', error);
      return [];
    });

    state.searchCache.set(cacheKey, request);
    return request;
  };

  const fetchTechnicalProduct = async (productUrl) => {
    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    const cacheKey = url.pathname;
    if (state.productCache.has(cacheKey)) return state.productCache.get(cacheKey);

    const request = (async () => {
      url.searchParams.set('view', 'pdl-logical-data');
      const previewId = previewThemeId();
      if (previewId) url.searchParams.set('preview_theme_id', previewId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json, text/plain, */*' },
      });
      if (!response.ok) throw new Error(`Produto HTTP ${response.status}`);
      const text = await response.text();
      const start = text.indexOf('{');
      const end = text.lastIndexOf('}');
      if (start < 0 || end <= start) throw new Error('Resposta técnica inválida');
      return JSON.parse(text.slice(start, end + 1));
    })().catch((error) => {
      console.warn('[Pedalokos] Falha ao ler produto relacionado.', url.pathname, error);
      return null;
    });

    state.productCache.set(cacheKey, request);
    return request;
  };

  const locationFlags = (product, variant) => {
    const tags = Array.isArray(product?.tags) ? product.tags : [];
    const locations = Array.isArray(variant?.locations) ? variant.locations : [];
    const availableLocationNames = locations
      .filter((location) => location && location.available !== false)
      .map((location) => location.name || '');
    const sourceNames = availableLocationNames.length > 0 ? availableLocationNames : tags;
    const haystack = normalize(sourceNames.join(' '));

    return {
      matrix: haystack.includes('matriz')
        || haystack.includes('sao leopoldo novo')
        || haystack.includes('sao leopoldo'),
      poa: haystack.includes('porto alegre') || /\bpoa\b/.test(haystack),
    };
  };

  const exactSkuVariants = (products, sku) => {
    const skuKey = normalize(sku);
    const records = [];

    products.forEach((product) => {
      if (!product) return;
      (product.variants || []).forEach((variant) => {
        if (!variant || normalize(variant.sku) !== skuKey || !variant.available) return;
        records.push({ product, variant, flags: locationFlags(product, variant) });
      });
    });

    return records;
  };

  const ensureOriginalBadgeState = (block) => {
    const badge = block.querySelector('.location-badge');
    const label = badge?.querySelector('.location-badge__label');
    if (!badge || !label) return null;

    if (!badge.dataset.pdlOriginalLabel) {
      badge.dataset.pdlOriginalLabel = String(label.textContent || '').trim();
      badge.dataset.pdlOriginalAria = badge.getAttribute('aria-label') || '';
      badge.dataset.pdlOriginalTitle = badge.getAttribute('title') || '';
    }

    return { badge, label };
  };

  const setFormVariant = (block, variantId) => {
    if (!variantId) return;
    const scope = block.closest('.product-details') || block.closest('.shopify-section') || document;
    scope.querySelectorAll('product-form-component input[name="id"]').forEach((input) => {
      input.value = String(variantId);
      input.dataset.pdlLogicalVariantId = String(variantId);
    });
  };

  const showBothStores = (block, matrixVariantId) => {
    const badgeState = ensureOriginalBadgeState(block);
    if (!badgeState) return;
    const { badge, label } = badgeState;
    const bothStoresLabel = block.dataset.pdlBothStoresLabel || 'Disponível em ambas as lojas';
    const bothStoresTitle = block.dataset.pdlBothStoresTitle || 'Disponível em São Leopoldo e Porto Alegre';
    label.textContent = bothStoresLabel;
    badge.setAttribute('aria-label', bothStoresLabel);
    badge.setAttribute('title', bothStoresTitle);
    badge.classList.add('location-badge--both-stores');

    const helper = block.querySelector('[data-pdl-location-helper]');
    if (helper) helper.hidden = false;
    block.dataset.pdlBothStores = 'true';
    setFormVariant(block, matrixVariantId);
  };

  const restoreNativeState = (block, selectedVariantId) => {
    const badgeState = ensureOriginalBadgeState(block);
    if (badgeState) {
      const { badge, label } = badgeState;
      label.textContent = badge.dataset.pdlOriginalLabel || label.textContent;
      badge.setAttribute('aria-label', badge.dataset.pdlOriginalAria || '');
      if (badge.dataset.pdlOriginalTitle) badge.setAttribute('title', badge.dataset.pdlOriginalTitle);
      else badge.removeAttribute('title');
      badge.classList.remove('location-badge--both-stores');
    }

    const helper = block.querySelector('[data-pdl-location-helper]');
    if (helper) helper.hidden = true;
    delete block.dataset.pdlBothStores;
    setFormVariant(block, selectedVariantId);
  };

  const candidateProductUrls = async (product, selectedVariant) => {
    const urls = new Set([`${routeRoot()}products/${product.handle}`]);

    const skuResults = await searchProducts(selectedVariant.sku, 'variants.sku,title');
    skuResults.forEach((result) => {
      if (result?.url) urls.add(result.url);
    });

    if (urls.size < 2) {
      const titleResults = await searchProducts(product.title, 'title,variants.sku');
      titleResults.forEach((result) => {
        if (result?.url) urls.add(result.url);
      });
    }

    return [...urls];
  };

  const evaluateBlock = async (block, forcedVariantId = '') => {
    const product = parseBlockProduct(block);
    if (!product) return;

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const selectedId = String(forcedVariantId || currentVariantId(block, product));
    const selectedVariant = variants.find((variant) => String(variant.id) === selectedId)
      || variants.find((variant) => variant.available)
      || variants[0]
      || null;

    if (!selectedVariant?.sku) {
      restoreNativeState(block, selectedVariant?.id || selectedId);
      return;
    }

    const requestId = String((Number(block.dataset.pdlRequestId || 0) + 1));
    block.dataset.pdlRequestId = requestId;
    block.setAttribute('aria-busy', 'true');

    try {
      const urls = await candidateProductUrls(product, selectedVariant);
      const products = (await Promise.all(urls.map(fetchTechnicalProduct))).filter(Boolean);
      if (block.dataset.pdlRequestId !== requestId) return;

      if (!products.some((candidate) => String(candidate.id) === String(product.id))) products.push(product);

      const records = exactSkuVariants(products, selectedVariant.sku);
      const matrixRecord = records
        .filter((record) => record.flags.matrix)
        .sort((a, b) => Number(b.variant.inventory_quantity || 0) - Number(a.variant.inventory_quantity || 0))[0];
      const poaRecord = records
        .filter((record) => record.flags.poa)
        .sort((a, b) => Number(b.variant.inventory_quantity || 0) - Number(a.variant.inventory_quantity || 0))[0];

      if (matrixRecord && poaRecord) {
        showBothStores(block, matrixRecord.variant.id);
      } else {
        restoreNativeState(block, selectedVariant.id);
      }
    } catch (error) {
      console.warn('[Pedalokos] Não foi possível unificar a localização na página do produto.', error);
      restoreNativeState(block, selectedVariant.id);
    } finally {
      if (block.dataset.pdlRequestId === requestId) block.removeAttribute('aria-busy');
    }
  };

  const bindBlock = (block) => {
    if (state.boundBlocks.has(block)) return;
    state.boundBlocks.add(block);
    ensureOriginalBadgeState(block);
    evaluateBlock(block);

    const section = block.closest('.shopify-section') || document;
    section.addEventListener('variant:update', (event) => {
      const variantId = event.detail?.resource?.id;
      if (!variantId) return;
      evaluateBlock(block, String(variantId));
    });
  };

  const bindAll = () => {
    document.querySelectorAll('[data-pdl-product-location]').forEach(bindBlock);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  } else {
    bindAll();
  }

  document.addEventListener('shopify:section:load', bindAll);
})();
